import * as React from "react";
import { getExchangeLogo, EXCHANGES, forgeTokenURL } from "@cede/utils";
import { BaseIcon, BaseIconProps } from "@/components/icons/primitives/BaseIcon";

export interface TokenIconProps extends Omit<BaseIconProps, "children"> {
  /**
   * Token symbol (e.g., 'BTC', 'ETH', 'USDT')
   */
  symbol: string;

  /**
   * Token icon URL (can be from CMS or external source)
   * If not provided, will show fallback
   */
  iconUrl?: string | null;

  /**
   * Exchange ID to resolve exchange logo as fallback (e.g., 'polymarket', 'binance')
   * When provided and iconUrl is missing, the exchange logo is used instead of the symbol fallback.
   */
  exchangeId?: string | null;

  /**
   * Fallback icon to display if logo fails to load
   */
  fallback?: React.ReactNode;

  /**
   * Fallback URL to attempt when the main icon fails to load
   */
  fallbackIconUrl?: string;

  /**
   * Whether to show token symbol as fallback
   * @default true
   */
  showSymbolFallback?: boolean;
}

/**
 * Token icon component that displays token logos with automatic fallback
 *
 * Provides a unified interface for displaying token icons with
 * automatic fallback to first letter of token symbol.
 *
 * @example
 * ```tsx
 * // Basic usage with CMS icon
 * <TokenIcon
 *   symbol="BTC"
 *   iconUrl={campaign.icon}
 *   size="md"
 * />
 *
 * // With underlay background
 * <TokenIcon
 *   symbol="ETH"
 *   iconUrl={tokenIconUrl}
 *   size="lg"
 *   withUnderlay
 *   underlayClassName="bg-blue-500/10"
 * />
 *
 * // Interactive with badge
 * <TokenIcon
 *   symbol="USDT"
 *   iconUrl={iconUrl}
 *   interactive
 *   badge={3}
 *   onClick={() => handleClick()}
 * />
 *
 * // With custom fallback
 * <TokenIcon
 *   symbol="UNKNOWN"
 *   fallback={<QuestionMark />}
 * />
 * ```
 */
export const TokenIcon = React.forwardRef<SVGSVGElement, TokenIconProps>(
  ({ symbol, iconUrl, exchangeId, fallback, fallbackIconUrl, showSymbolFallback = true, ...baseIconProps }, ref) => {
    const DEFAULT_FALLBACK_ICON_URL = "https://cdn.cedelabs.io/token/cede-fallback.svg";
    const isPolymarket = exchangeId === EXCHANGES.POLYMARKET;
    const exchangeLogoUrl = isPolymarket ? getExchangeLogo(exchangeId) : null;
    const symbolCdnUrl = symbol ? forgeTokenURL(symbol) : null;
    const srcOptions = React.useMemo(() => {
      const unique: string[] = [];
      [
        isPolymarket ? exchangeLogoUrl : null,
        iconUrl,
        fallbackIconUrl,
        symbolCdnUrl,
        DEFAULT_FALLBACK_ICON_URL,
      ].forEach((src) => {
        if (src && !unique.includes(src)) {
          unique.push(src);
        }
      });
      return unique;
    }, [iconUrl, exchangeLogoUrl, fallbackIconUrl, symbolCdnUrl]);

    const [srcIndex, setSrcIndex] = React.useState(0);

    // Reset error state when iconUrl changes
    React.useEffect(() => {
      setSrcIndex(0);
    }, [srcOptions]);

    const currentSrc = srcOptions[srcIndex];

    const handleError = () => {
      const hasNext = srcIndex < srcOptions.length - 1;
      if (hasNext) {
        setSrcIndex((prev) => prev + 1);
        return;
      }

      setSrcIndex(srcOptions.length); // no more sources to try
    };

    // If no URL provided or image failed to load, show fallback
    if (!currentSrc) {
      if (fallback) {
        return (
          <BaseIcon ref={ref} {...baseIconProps}>
            {fallback}
          </BaseIcon>
        );
      }

      // Default fallback: first letter of token symbol
      if (showSymbolFallback) {
        const fallbackLetter = symbol.charAt(0).toUpperCase();
        return (
          <BaseIcon ref={ref} {...baseIconProps}>
            <text x="50%" y="50%" textAnchor="middle" dy="0.35em" fontSize="12" fontWeight="600" fill="currentColor">
              {fallbackLetter}
            </text>
          </BaseIcon>
        );
      }
    }

    return (
      <BaseIcon ref={ref} {...baseIconProps}>
        <image href={currentSrc} width="24" height="24" onError={handleError} aria-label={`${symbol} logo`} />
      </BaseIcon>
    );
  },
);

TokenIcon.displayName = "TokenIcon";
